import React, { useState, createElement } from 'react';
import { Header } from './components/Header';
import { QuestionCard } from './components/QuestionCard';
import { NavigationArrows } from './components/NavigationArrows';
import { ActionButtons } from './components/ActionButtons';
import { VideoModal } from './components/VideoModal';
import { ChatModal } from './components/ChatModal';
import { ExportButton } from './components/ExportButton';
import { questionCards } from './data';
import { CardChatHistory, ChatMessage } from './types';
import { jsPDF } from 'jspdf';
// Helper function to convert markdown to formatted PDF text with proper page breaks
function formatMarkdownForPDF(text: string, pdf: jsPDF, x: number, y: number, maxWidth: number, pageHeight: number, margin: number): number {
  let currentY = y;
  const lineHeight = 5;
  const bottomMargin = 40; // Space to leave at bottom for footer
  // Helper to check if we need a new page
  const checkPageBreak = (additionalLines: number = 1) => {
    if (currentY + additionalLines * lineHeight > pageHeight - bottomMargin) {
      pdf.addPage();
      currentY = margin;
      return true;
    }
    return false;
  };
  // Split by lines
  const lines = text.split('\n');
  lines.forEach(line => {
    // Skip empty lines but add spacing
    if (!line.trim()) {
      currentY += lineHeight / 2;
      return;
    }
    // Handle headings (# ## ###)
    if (line.trim().startsWith('#')) {
      const level = line.match(/^#+/)?.[0].length || 1;
      const headingText = line.replace(/^#+\s*/, '');
      checkPageBreak(2);
      pdf.setFont('helvetica', 'bold');
      if (level === 1) pdf.setFontSize(14);else if (level === 2) pdf.setFontSize(12);else pdf.setFontSize(11);
      const textLines = pdf.splitTextToSize(headingText, maxWidth);
      textLines.forEach((textLine: string) => {
        checkPageBreak();
        pdf.text(textLine, x, currentY);
        currentY += lineHeight + 1;
      });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      currentY += 2;
      return;
    }
    // Handle bullet points (- or *)
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      const bulletText = line.trim().substring(2);
      // Remove any markdown bold/italic markers for cleaner output
      const cleanText = bulletText.replace(/\*\*/g, '').replace(/\*/g, '');
      pdf.setFont('helvetica', 'normal');
      const textLines = pdf.splitTextToSize('• ' + cleanText, maxWidth - 5);
      textLines.forEach((textLine: string) => {
        checkPageBreak();
        pdf.text(textLine, x + 5, currentY);
        currentY += lineHeight;
      });
      return;
    }
    // Handle numbered lists (1. 2. etc)
    if (/^\d+\.\s/.test(line.trim())) {
      // Remove any markdown bold/italic markers
      const cleanText = line.trim().replace(/\*\*/g, '').replace(/\*/g, '');
      pdf.setFont('helvetica', 'normal');
      const textLines = pdf.splitTextToSize(cleanText, maxWidth - 5);
      textLines.forEach((textLine: string) => {
        checkPageBreak();
        pdf.text(textLine, x + 5, currentY);
        currentY += lineHeight;
      });
      return;
    }
    // Handle bold text - check if entire line is bold or has bold sections
    if (line.includes('**')) {
      // For simplicity, remove ** markers and make entire line bold if it starts with **
      if (line.trim().startsWith('**')) {
        const cleanText = line.replace(/\*\*/g, '');
        pdf.setFont('helvetica', 'bold');
        const textLines = pdf.splitTextToSize(cleanText, maxWidth);
        textLines.forEach((textLine: string) => {
          checkPageBreak();
          pdf.text(textLine, x, currentY);
          currentY += lineHeight;
        });
        pdf.setFont('helvetica', 'normal');
      } else {
        // Mixed bold/normal - just remove markers and use normal font
        const cleanText = line.replace(/\*\*/g, '');
        pdf.setFont('helvetica', 'normal');
        const textLines = pdf.splitTextToSize(cleanText, maxWidth);
        textLines.forEach((textLine: string) => {
          checkPageBreak();
          pdf.text(textLine, x, currentY);
          currentY += lineHeight;
        });
      }
      return;
    }
    // Regular text - remove any remaining markdown
    const cleanText = line.replace(/\*\*/g, '').replace(/\*/g, '');
    pdf.setFont('helvetica', 'normal');
    const textLines = pdf.splitTextToSize(cleanText, maxWidth);
    textLines.forEach((textLine: string) => {
      checkPageBreak();
      pdf.text(textLine, x, currentY);
      currentY += lineHeight;
    });
  });
  return currentY;
}
export function App() {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatHistories, setChatHistories] = useState<CardChatHistory>({});
  const currentCard = questionCards[currentCardIndex];
  const currentMessages = chatHistories[currentCardIndex] || [];
  const handlePrevious = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };
  const handleNext = () => {
    if (currentCardIndex < questionCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };
  const handleSendMessage = async (message: string) => {
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: message
    };
    const updatedMessages = [...currentMessages, newUserMessage];
    setChatHistories({
      ...chatHistories,
      [currentCardIndex]: updatedMessages
    });
    try {
      // Prepare messages with system context
      const systemMessage = {
        role: 'system',
        content: `You are a thoughtful life coach helping someone navigate career and life transitions. The topic is "${currentCard.subject}". The user is reflecting on: "${currentCard.question}". Provide empathetic, practical guidance that helps them explore their thoughts and find clarity.`
      };
      const apiMessages = [systemMessage, ...updatedMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }))];
      console.log('Sending request to Magic Patterns proxy...');
      console.log('Current domain:', window.location.origin);
      // Call Magic Patterns OpenAI proxy (no API key needed - handled by proxy)
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 500
        })
      });
      console.log('Response status:', response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        // Provide more helpful error messages
        let errorMessage = 'Unable to connect to the AI service.';
        if (response.status === 401 || response.status === 403) {
          errorMessage = 'Access denied. The domain http://video.bridgesoflife.online may need to be whitelisted with the Magic Patterns proxy service. Please contact support to enable access for this domain.';
        } else if (response.status === 429) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (response.status >= 500) {
          errorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
        } else if (errorData.error) {
          errorMessage = `Error: ${errorData.error}`;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      console.log('Received AI response successfully');
      const aiResponse: ChatMessage = {
        role: 'assistant',
        content: data.choices[0].message.content
      };
      setChatHistories(prev => ({
        ...prev,
        [currentCardIndex]: [...prev[currentCardIndex], aiResponse]
      }));
    } catch (error) {
      console.error('Chat error:', error);
      let errorContent = 'I apologize, but I encountered an issue connecting to the AI service. ';
      // Check if it's a network/CORS error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorContent = 'Unable to connect to the AI service. This appears to be a network or CORS (Cross-Origin) issue.\n\n';
        errorContent += '**Possible causes:**\n';
        errorContent += '• The domain http://video.bridgesoflife.online needs to be whitelisted with the Magic Patterns proxy service\n';
        errorContent += '• Your site is using HTTP instead of HTTPS, which can cause security restrictions on mobile browsers\n';
        errorContent += '• Network connectivity issues\n\n';
        errorContent += '**To fix this:** Contact Magic Patterns support to whitelist your domain, or ensure your site is deployed with HTTPS enabled.';
      } else if (error instanceof Error) {
        errorContent += error.message;
      } else {
        errorContent += 'Please try again in a moment.';
      }
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: errorContent
      };
      setChatHistories(prev => ({
        ...prev,
        [currentCardIndex]: [...prev[currentCardIndex], errorMessage]
      }));
    }
  };
  const handleExportPDF = async () => {
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      // Load logo once
      let logoBase64: string | null = null;
      const logoUrl = 'https://uploadthingy.s3.us-west-1.amazonaws.com/9jjij9zTdgXHXrJyRN6ABS/BoL_logo2.png';
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        logoBase64 = reader.result as string;
      } catch (error) {
        console.error('Error loading logo:', error);
      }
      // Loop through all cards
      questionCards.forEach((card, cardIndex) => {
        const messages = chatHistories[cardIndex] || [];
        // Add new page for each card (except first)
        if (cardIndex > 0) {
          pdf.addPage();
        }
        let yPosition = margin;
        // Add header with logo and title
        if (logoBase64) {
          pdf.addImage(logoBase64, 'PNG', margin, yPosition, 30, 30);
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Bridges of Life', margin + 35, yPosition + 15);
          yPosition += 40;
        } else {
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Bridges of Life', margin, yPosition);
          yPosition += 15;
        }
        // Add separator line
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
        // Add card subject
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text(card.subject, margin, yPosition);
        yPosition += 10;
        // Add card number
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Card ${cardIndex + 1} of ${questionCards.length}`, margin, yPosition);
        yPosition += 8;
        // Add question
        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);
        const questionLines = pdf.splitTextToSize(card.question, pageWidth - 2 * margin);
        pdf.text(questionLines, margin, yPosition);
        yPosition += questionLines.length * 5 + 10;
        // Add another separator
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 10;
        // Add chat messages or empty state
        if (messages.length === 0) {
          // No chat history - display message
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(120, 120, 120);
          pdf.text('No conversation yet for this card.', margin, yPosition);
        } else {
          // Has chat history - display messages with markdown formatting
          pdf.setFontSize(10);
          messages.forEach(msg => {
            // Check if we need a new page before adding message header
            if (yPosition > pageHeight - 40) {
              pdf.addPage();
              yPosition = margin;
            }
            // Set color based on role
            if (msg.role === 'user') {
              pdf.setTextColor(0, 0, 255);
              pdf.setFont('helvetica', 'bold');
              pdf.text('You:', margin, yPosition);
            } else {
              pdf.setTextColor(0, 128, 0);
              pdf.setFont('helvetica', 'bold');
              pdf.text('AI Assistant:', margin, yPosition);
            }
            yPosition += 6;
            // Add message content with markdown formatting
            pdf.setTextColor(0, 0, 0);
            yPosition = formatMarkdownForPDF(msg.content, pdf, margin + 5, yPosition, pageWidth - 2 * margin - 5, pageHeight, margin);
            yPosition += 5;
          });
        }
        // Add footer with date on each page
        const footer = `Generated on ${new Date().toLocaleString()}`;
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(footer, margin, pageHeight - 10);
      });
      // Save the PDF
      pdf.save(`Bridges-of-Life-All-Cards-${Date.now()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" style={{
    maxWidth: '428px',
    margin: '0 auto'
  }} data-id="element-0">
      <Header data-id="element-1" />

      <ExportButton onClick={handleExportPDF} data-id="element-2" />

      <div className="flex-1 flex flex-col" data-id="element-3">
        <QuestionCard card={currentCard} data-id="element-4" />

        <NavigationArrows currentIndex={currentCardIndex} totalCards={questionCards.length} onPrevious={handlePrevious} onNext={handleNext} data-id="element-5" />

        <ActionButtons onWatchVideo={() => setShowVideoModal(true)} onOpenChat={() => setShowChatModal(true)} data-id="element-6" />
      </div>

      {showVideoModal && <VideoModal videoUrl={currentCard.videoUrl} onClose={() => setShowVideoModal(false)} data-id="element-7" />}

      {showChatModal && <ChatModal cardIndex={currentCardIndex} cardSubject={currentCard.subject} cardQuestion={currentCard.question} messages={currentMessages} onClose={() => setShowChatModal(false)} onSendMessage={handleSendMessage} data-id="element-8" />}
    </div>;
}